import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { getErrorMessage } from '../utils/errorMessage';
import {
  FaArrowLeft,
  FaBullhorn,
  FaCloudUploadAlt,
  FaCheckCircle,
  FaChartLine,
  FaUsers,
  FaHome,
  FaListUl,
} from 'react-icons/fa';

const PERIOD_OPTIONS = [
  { value: 3, label: '3 måneder' },
  { value: 6, label: '6 måneder' },
  { value: 12, label: '12 måneder' },
];

const ACCEPTED_MIME = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_BYTES = 5 * 1024 * 1024;

/**
 * Annonceprodukter — SINGLE SOURCE OF TRUTH for både formular, info-kort,
 * upload-krav og live-preview. Ændringer her opdaterer alt.
 *
 * Bottom Banner: klassisk fastformat banner (1200×200, 6:1) på forsiden,
 * vist for gæster og gratis-brugere.
 *
 * Sponsor-række: fuldbredde banner mellem opskrifter, vist for Light-brugere.
 * Bruger `<img w-full h-auto>` så billedet aldrig beskæres.
 */
const PRODUCTS = {
  bottom_banner: {
    id: 'bottom_banner',
    label: 'Bottom Banner',
    icon: FaHome,
    placement: 'Forsiden',
    audience: 'Gæster og Gratis-brugere',
    dimensions: '1200 × 200 px (6:1)',
    dimensionsShort: '1200 × 200 px',
    maxWidth: 1200,
    maxHeight: 200,
    aspectHint: '6:1 (bred og lav)',
    helpText: 'Anbefalet: præcis 1200 × 200 px (6:1). Vises som fast bundbanner på forsiden.',
    previewLabel: 'Forhåndsvisning — sådan vises banneret på forsiden',
  },
  sponsor_row: {
    id: 'sponsor_row',
    label: 'Sponsor-række',
    icon: FaListUl,
    placement: 'Mellem opskrifter i opskriftslisten',
    audience: 'Light-brugere',
    dimensions: 'Maks. 1200 × 400 px',
    dimensionsShort: 'Op til 1200 × 400 px',
    maxWidth: 1200,
    maxHeight: 400,
    aspectHint: 'Fri højde (anbefalet 250–350 px)',
    helpText: 'Fuld bredde, ingen beskæring. Højden tilpasser sig billedets naturlige proportioner. Anbefalet: 1200 × ~300 px.',
    previewLabel: 'Forhåndsvisning — sådan vises rækken mellem opskrifter',
  },
};

const initialForm = {
  product_type: 'bottom_banner',
  company: '',
  contact_person: '',
  email: '',
  phone: '',
  period_months: 3,
  click_link: '',
  comment: '',
};

const AdvertisePage = () => {
  const [form, setForm] = useState(initialForm);
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [imageDims, setImageDims] = useState(null); // {width, height}
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const product = PRODUCTS[form.product_type] || PRODUCTS.bottom_banner;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === 'period_months' ? parseInt(value, 10) : value,
    }));
  };

  const handleProductChange = (productId) => {
    setForm((prev) => ({ ...prev, product_type: productId }));
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ACCEPTED_MIME.includes(f.type)) {
      toast.error('Filtype ikke tilladt. Brug JPG, PNG eller WebP.');
      e.target.value = '';
      return;
    }
    if (f.size > MAX_FILE_BYTES) {
      toast.error('Filen er for stor. Max 5 MB.');
      e.target.value = '';
      return;
    }
    setFile(f);
    const url = URL.createObjectURL(f);
    setFilePreview(url);
    // Read image dimensions for soft-warning if they don't match specs.
    const img = new Image();
    img.onload = () => setImageDims({ width: img.width, height: img.height });
    img.src = url;
  };

  const dimensionWarning = useMemo(() => {
    if (!imageDims) return null;
    const { width, height } = imageDims;
    if (form.product_type === 'bottom_banner') {
      // Recommend 1200×200 exact ratio 6:1 with 10% tolerance
      const ratio = width / height;
      if (ratio < 5.4 || ratio > 6.6) {
        return `Billedet er ${width}×${height} — anbefalet er 1200 × 200 px (6:1). Det kan se strakt eller sammenpresset ud.`;
      }
    } else if (form.product_type === 'sponsor_row') {
      if (width > 1200) {
        return `Billedet er ${width} px bredt — det bliver skaleret ned til maks. 1200 px. Overvej at optimere før upload.`;
      }
      if (height > 400) {
        return `Billedet er ${height} px højt — det fylder meget i feed'et. Anbefalet højde er 250–350 px.`;
      }
    }
    return null;
  }, [imageDims, form.product_type]);

  const uploadBanner = async () => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await axios.post(`${API}/upload?folder=ad-requests`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data?.url || res.data?.image_url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Vælg en bannerfil først');
      return;
    }
    if (!form.company.trim() || !form.contact_person.trim() || !form.email.trim()) {
      toast.error('Udfyld firma, kontaktperson og e-mail');
      return;
    }
    try {
      setSubmitting(true);
      setUploading(true);
      const imageUrl = await uploadBanner();
      setUploading(false);

      await axios.post(`${API}/ad-requests`, {
        ...form,
        image_url: imageUrl,
      });
      setSubmitted(true);
      toast.success('Tak! Din annonceforespørgsel er modtaget.');
    } catch (err) {
      console.error('Submit error:', err);
      toast.error(
        getErrorMessage(err, 'Noget gik galt. Prøv igen eller send en mail til kontakt@slushbook.dk')
      );
    } finally {
      setSubmitting(false);
      setUploading(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="advertise-success">
        <div className="bg-white rounded-3xl p-10 shadow-sm border border-gray-100">
          <FaCheckCircle className="text-emerald-500 text-6xl mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-3 text-gray-800">Tak!</h1>
          <p className="text-gray-600 mb-6">
            Din <strong>{product.label.toLowerCase()}</strong>-forespørgsel er modtaget og
            afventer godkendelse. Vi vender tilbage til <strong>{form.email}</strong> så
            hurtigt vi kan.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-full shadow-md hover:shadow-lg transition-all"
            data-testid="advertise-success-home"
          >
            <FaArrowLeft />
            Tilbage til SlushBook
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-10" data-testid="advertise-page">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-cyan-600 hover:text-cyan-700 mb-6 text-sm font-medium"
      >
        <FaArrowLeft />
        Tilbage til SlushBook
      </Link>

      {/* Hero */}
      <div className="bg-gradient-to-br from-cyan-500 via-blue-500 to-purple-600 text-white rounded-3xl p-8 md:p-10 shadow-xl mb-8">
        <div className="flex items-center gap-3 mb-3">
          <FaBullhorn className="text-3xl" />
          <span className="uppercase text-xs font-bold tracking-widest opacity-90">
            Annoncer på SlushBook
          </span>
        </div>
        <h1 className="text-3xl md:text-5xl font-bold mb-3 leading-tight">
          Denne plads kan blive din.
        </h1>
        <p className="text-base md:text-lg opacity-95 max-w-2xl">
          Vælg mellem to annonceprodukter herunder — vi vender tilbage med priser og
          næste ledige slot.
        </p>
        <a
          href="https://vweb.info/bannerann.html"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="advertise-more-info-link"
          className="mt-6 inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 backdrop-blur-sm border border-white/30 text-white font-semibold px-5 py-2.5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-white/60"
        >
          Læs mere om bannerannoncering
          <span aria-hidden>↗</span>
        </a>
      </div>

      {/* Info-kort for begge annonceprodukter */}
      <div className="grid md:grid-cols-2 gap-4 mb-10">
        {Object.values(PRODUCTS).map((p) => {
          const Icon = p.icon;
          return (
            <div
              key={p.id}
              className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
              data-testid={`advertise-info-${p.id}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <Icon className="text-cyan-500 text-2xl" />
                <h3 className="font-bold text-gray-800 text-lg">{p.label}</h3>
              </div>
              <dl className="text-sm space-y-1.5">
                <InfoRow label="Placering" value={p.placement} />
                <InfoRow label="Målgruppe" value={p.audience} />
                <InfoRow label="Bannerformat" value={p.dimensions} />
                <InfoRow label="Højde" value={p.aspectHint} />
              </dl>
            </div>
          );
        })}
      </div>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100 space-y-6"
        data-testid="advertise-form"
      >
        <h2 className="text-xl font-bold text-gray-800">Send din annonceforespørgsel</h2>

        {/* 1. Vælg annonceprodukt */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            1. Vælg annonceprodukt *
          </label>
          <div className="grid sm:grid-cols-2 gap-3">
            {Object.values(PRODUCTS).map((p) => {
              const Icon = p.icon;
              const selected = form.product_type === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => handleProductChange(p.id)}
                  data-testid={`advertise-product-${p.id}`}
                  aria-pressed={selected}
                  className={`text-left rounded-xl border-2 p-4 transition-all ${
                    selected
                      ? 'border-cyan-500 bg-cyan-50 ring-2 ring-cyan-200'
                      : 'border-gray-200 hover:border-cyan-300 bg-white'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <Icon className={`text-xl mt-0.5 ${selected ? 'text-cyan-600' : 'text-gray-400'}`} />
                    <div className="flex-1">
                      <div className={`font-semibold ${selected ? 'text-cyan-900' : 'text-gray-800'}`}>
                        {p.label}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">{p.placement}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{p.dimensionsShort}</div>
                    </div>
                    {selected && <FaCheckCircle className="text-cyan-500 flex-shrink-0" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* 2. Kontaktoplysninger */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">2. Kontaktoplysninger</label>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Firmanavn *" name="company" value={form.company} onChange={handleChange} required testid="advertise-company" />
            <Field label="Kontaktperson *" name="contact_person" value={form.contact_person} onChange={handleChange} required testid="advertise-contact" />
            <Field label="E-mail *" name="email" type="email" value={form.email} onChange={handleChange} required testid="advertise-email" />
            <Field label="Telefon" name="phone" value={form.phone} onChange={handleChange} testid="advertise-phone" />
          </div>
        </div>

        {/* 3. Periode + klik-link */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">3. Periode og klik-link</label>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Periode *</label>
              <select
                name="period_months"
                value={form.period_months}
                onChange={handleChange}
                required
                data-testid="advertise-period"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
              >
                {PERIOD_OPTIONS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
            <Field
              label="Klik-link (URL) *"
              name="click_link"
              type="url"
              value={form.click_link}
              onChange={handleChange}
              required
              placeholder="https://"
              testid="advertise-link"
            />
          </div>
        </div>

        {/* 4. Banner upload med dynamiske krav */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            4. Bannerfil * <span className="text-xs text-gray-500 font-normal">— {product.label}</span>
          </label>
          <div className="rounded-xl bg-cyan-50 border border-cyan-200 p-4 mb-3" data-testid="advertise-banner-specs">
            <div className="text-xs font-semibold text-cyan-900 uppercase tracking-wide mb-2">
              Krav til {product.label.toLowerCase()}
            </div>
            <ul className="text-sm text-cyan-900 space-y-1">
              <li>• Placering: <strong>{product.placement}</strong></li>
              <li>• Målgruppe: <strong>{product.audience}</strong></li>
              <li>• Format: <strong>{product.dimensions}</strong></li>
              <li>• Højde: <strong>{product.aspectHint}</strong></li>
              <li>• Filtyper: JPG, PNG eller WebP · maks. 5 MB</li>
            </ul>
            <p className="text-xs text-cyan-800 mt-2 italic">{product.helpText}</p>
          </div>
          <div className="border-2 border-dashed border-gray-300 rounded-xl p-5 hover:border-cyan-400 transition-colors">
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileChange}
              data-testid="advertise-banner-input"
              className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-cyan-50 file:text-cyan-700 hover:file:bg-cyan-100 cursor-pointer"
            />
            {filePreview && (
              <div className="mt-3 flex items-center gap-3 text-xs text-gray-600">
                <FaCloudUploadAlt className="text-cyan-500" />
                <span>Valgt: {file?.name}{imageDims && ` — ${imageDims.width}×${imageDims.height} px`}</span>
              </div>
            )}
            {dimensionWarning && (
              <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                ⚠ {dimensionWarning}
              </div>
            )}
          </div>
        </div>

        {/* 5. Live preview — matcher rigtige placeringer */}
        {filePreview && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              5. Forhåndsvisning
            </label>
            <div className="text-xs text-gray-500 mb-3">{product.previewLabel}</div>
            {form.product_type === 'bottom_banner' ? (
              <BottomBannerPreview src={filePreview} company={form.company || 'Dit firma'} />
            ) : (
              <SponsorRowPreview src={filePreview} company={form.company || 'Dit firma'} />
            )}
          </div>
        )}

        {/* 6. Kommentar */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">6. Kommentar</label>
          <textarea
            name="comment"
            value={form.comment}
            onChange={handleChange}
            rows={3}
            data-testid="advertise-comment"
            placeholder="Ønsker, særlige krav, kampagneperiode mm."
            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          data-testid="advertise-submit"
          className="w-full px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-full shadow-md transition-all active:scale-95 flex items-center justify-center gap-2"
        >
          {submitting
            ? (uploading ? 'Uploader banner…' : 'Sender forespørgsel…')
            : `Send annonceforespørgsel — ${product.label}`}
        </button>

        <p className="text-xs text-gray-500 text-center">
          Din forespørgsel oprettes med status <strong>“Afventer godkendelse”</strong>.
          Banneret bliver først aktivt når admin godkender det.
        </p>
      </form>

      {/* Fordele-kort */}
      <div className="grid md:grid-cols-3 gap-4 mt-10">
        <BenefitCard icon={FaUsers} title="Engageret publikum" text="Slush-elskere og hobby-baristaer i hele Norden." />
        <BenefitCard icon={FaChartLine} title="Målrettet visning" text="Bottom Banner rammer gæster/gratis. Sponsor-række rammer Light-brugere." />
        <BenefitCard icon={FaBullhorn} title="Fleksible perioder" text="3, 6 eller 12 måneders annoncering." />
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Sub-komponenter
// ────────────────────────────────────────────────────────────────────────────

const Field = ({ label, name, value, onChange, type = 'text', required, placeholder, testid }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
    />
  </div>
);

const InfoRow = ({ label, value }) => (
  <div className="flex items-baseline gap-2">
    <dt className="text-gray-500 min-w-[90px]">{label}:</dt>
    <dd className="text-gray-800 font-medium">{value}</dd>
  </div>
);

const BenefitCard = ({ icon: Icon, title, text }) => (
  <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
    <Icon className="text-cyan-500 text-xl mb-2" />
    <h3 className="font-bold text-gray-800 mb-1 text-sm">{title}</h3>
    <p className="text-xs text-gray-600">{text}</p>
  </div>
);

/**
 * Preview af bottom-banner — matcher hvordan det vises på forsiden.
 * Fast 6:1 aspect ratio, centreret, med sponsor-pill i hjørnet.
 */
const BottomBannerPreview = ({ src, company }) => (
  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200" data-testid="advertise-preview-bottom-banner">
    <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2 text-center">
      ↓ Forsiden — bundbanner
    </div>
    <div className="relative w-full mx-auto rounded-lg overflow-hidden shadow-md bg-white" style={{ aspectRatio: '6 / 1' }}>
      <img src={src} alt="banner preview" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute top-2 right-2 bg-white/95 text-[9px] font-semibold text-gray-700 uppercase tracking-wider px-2 py-0.5 rounded-full shadow-sm">
        Sponsoreret · {company}
      </div>
    </div>
  </div>
);

/**
 * Preview af sponsor-række — matcher SponsoredRowBlock.js: `w-full h-auto`,
 * ingen beskæring, naturlig aspect ratio, gradient overlay + sponsor-pill.
 */
const SponsorRowPreview = ({ src, company }) => (
  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200" data-testid="advertise-preview-sponsor-row">
    <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2 text-center">
      ↓ Opskriftsfeed — mellem opskrifter
    </div>
    {/* Simulér grid-kontekst: vis 3 fake opskriftskort ovenover for realisme */}
    <div className="grid grid-cols-4 gap-2 mb-2 opacity-40 pointer-events-none">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="aspect-square bg-gradient-to-br from-gray-200 to-gray-300 rounded" />
      ))}
    </div>
    <a
      className="col-span-full relative block overflow-hidden rounded-2xl shadow-sm no-underline"
      href="#preview"
      onClick={(e) => e.preventDefault()}
    >
      <img src={src} alt="sponsor-row preview" className="block w-full h-auto" />
      <div className="absolute inset-0 bg-gradient-to-r from-blue-900/70 via-blue-900/40 to-transparent pointer-events-none" />
      <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm text-[10px] font-semibold text-gray-700 uppercase tracking-wider px-2.5 py-1 rounded-full shadow-sm">
        Sponsoreret · {company}
      </div>
    </a>
    <div className="grid grid-cols-4 gap-2 mt-2 opacity-40 pointer-events-none">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="aspect-square bg-gradient-to-br from-gray-200 to-gray-300 rounded" />
      ))}
    </div>
  </div>
);

export default AdvertisePage;
